/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable eqeqeq */
import { useEffect, useState, useRef } from "react";
import { useSpring, animated, useTransition } from "@react-spring/web";

import Chessboard from "../components/chessboard/Chessboard";
import { analyzeGame } from "../api/analyzeGame";

import useSound from 'use-sound';
import correctSound from '../assets/sounds/correct.wav';
import wrongSound from '../assets/sounds/wrong.wav';


// Milestone thresholds and their banner messages
const MILESTONES = {
    3:  "On fire! 🔥",
    5:  "Unstoppable! ⚡",
    10: "LEGENDARY 👑",
    15: "INCREDIBLE 🌟",
    20: "GRANDMASTER MODE 🏆",
};

function isMilestone(n) {
    return Object.keys(MILESTONES).map(Number).includes(n);
}

function getStreakStyle(streak) {
    if (streak >= 10) return "bg-red-500/20 text-red-400 ring-2 ring-red-500 animate-pulse";
    if (streak >= 5)  return "bg-amber-500/20 text-amber-400";
    if (streak >= 3)  return "bg-orange-500/20 text-orange-400";
    return "";
}


function Vision({ userData }) {
    const gameLength = 30;

    const [randomSquare, setRandomSquare] = useState(null);
    const [clickedSquare, setClickedSquare] = useState(null);
    const [isGameStarted, setIsGameStarted] = useState(false);
    const [timeLeft, setTimeLeft] = useState(gameLength);
    const [gameResults, setGameResults] = useState([]);
    const [score, setScore] = useState(0);
    const [actionStartTime, setActionStartTime] = useState(performance.now());
    const [accuracy, setAccuracy] = useState(0);
    const [speed, setSpeed] = useState(5);
    const [totalClicks, setTotalClicks] = useState(0);

    const [saveError, setSaveError] = useState(false);

    // --- Streak state ---
    const [streak, setStreak] = useState(0);
    const [bannerMessage, setBannerMessage] = useState(null);
    const bannerTimerRef = useRef(null);

    // Track "pop" trigger so we can re-trigger on each milestone
    const [popKey, setPopKey] = useState(0);

    // Tiered correct sounds: pitch escalates with streak tier (rate = playback speed/pitch)
    const [playC0] = useSound(correctSound, { volume: 0.10, rate: 1.00 }); // no streak
    const [playC1] = useSound(correctSound, { volume: 0.11, rate: 1.10 }); // 3–4
    const [playC2] = useSound(correctSound, { volume: 0.12, rate: 1.25 }); // 5–9
    const [playC3] = useSound(correctSound, { volume: 0.13, rate: 1.50 }); // 10–14
    const [playC4] = useSound(correctSound, { volume: 0.14, rate: 1.75 }); // 15–19
    const [playC5] = useSound(correctSound, { volume: 0.15, rate: 2.00 }); // 20+

    // Harmony tones: a perfect fifth above each tier (rate × 1.5), played on milestones
    const [playH1] = useSound(correctSound, { volume: 0.05, rate: 1.65 });
    const [playH2] = useSound(correctSound, { volume: 0.05, rate: 1.875 });
    const [playH3] = useSound(correctSound, { volume: 0.05, rate: 2.25 });
    const [playH4] = useSound(correctSound, { volume: 0.05, rate: 2.625 });
    const [playH5] = useSound(correctSound, { volume: 0.06, rate: 3.00 });

    const [playWrong] = useSound(wrongSound, { volume: 0.12, rate: 0.85 }); // deeper = more "oof"

    const audioCtxRef = useRef(null);

    // Returns [basePlay, harmonyPlay] for the current streak
    const getStreakAudio = (s) => {
        if (s >= 20) return [playC5, playH5];
        if (s >= 15) return [playC4, playH4];
        if (s >= 10) return [playC3, playH3];
        if (s >= 5)  return [playC2, playH2];
        if (s >= 3)  return [playC1, playH1];
        return [playC0, null];
    };

    // Web Audio reverb burst for the tier-20 "GRANDMASTER" milestone
    const playReverbBurst = () => {
        try {
            const ctx = audioCtxRef.current || (audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)());
            const bufferSize = ctx.sampleRate * 1.2;
            const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
            const data = buffer.getChannelData(0);
            for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 2);
            const source = ctx.createBufferSource();
            source.buffer = buffer;
            const gain = ctx.createGain();
            gain.gain.setValueAtTime(0.18, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.2);
            source.connect(gain);
            gain.connect(ctx.destination);
            source.start();
        } catch (_) {}
    };

    // --- Spring for streak badge "pop" animation ---
    const [badgeSpring, badgeApi] = useSpring(() => ({
        scale: 1,
        config: { tension: 400, friction: 12 },
    }));

    // Re-trigger pop whenever popKey changes
    useEffect(() => {
        if (popKey === 0) return;
        badgeApi.start({
            from: { scale: 1.5 },
            to:   { scale: 1 },
        });
    }, [popKey]);

    // --- Transition for the banner ---
    const bannerTransition = useTransition(bannerMessage, {
        from:   { opacity: 0, transform: "translateY(-20px)" },
        enter:  { opacity: 1, transform: "translateY(0px)" },
        leave:  { opacity: 0, transform: "translateY(-20px)" },
        config: { tension: 300, friction: 20 },
    });

    // Helper: show a banner for 1.5s then auto-dismiss
    const showBanner = (msg) => {
        if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
        setBannerMessage(msg);
        bannerTimerRef.current = setTimeout(() => setBannerMessage(null), 1500);
    };

    useEffect(() => {
        // Handles logic for the timer
        let timer;
        if (isGameStarted && timeLeft > 0) {
            timer = setTimeout(() => {
                setTimeLeft(timeLeft - 1);
            }, 1000);
        } else if (timeLeft === 0) {
            if (isGameStarted){
                endGame();
            }

        }
        return () => clearTimeout(timer);
    }, [isGameStarted, timeLeft]);

    useEffect(() => {
        if (isGameStarted) {
            // Handles logic for when a square is clicked and game is started
            const elapsedTime = performance.now() - actionStartTime;
            setTotalClicks(totalClicks + 1);
            setGameResults([...gameResults, {"clicked": clickedSquare, "goal": randomSquare, "time": Math.round(elapsedTime)}]);
            if (clickedSquare === randomSquare) {
                console.log("New Accuracy:", (score+1) / (totalClicks+1));
                console.log("New Speed:", (performance.now() - actionStartTime)/(1000*score+1));
                setAccuracy((score+1) / (totalClicks+1));
                setSpeed((performance.now() - actionStartTime)/(1000*(score+1)));

                const newStreak = streak + 1;
                const [basePlay, harmonyPlay] = getStreakAudio(newStreak);

                basePlay();
                setRandomSquare(generateRandomSquare());
                setScore(score + 1);
                setStreak(newStreak);

                if (isMilestone(newStreak)) {
                    setPopKey(k => k + 1);
                    showBanner(MILESTONES[newStreak]);

                    if (newStreak >= 20) {
                        // GRANDMASTER: stutter + reverb tail
                        setTimeout(() => basePlay(), 80);
                        setTimeout(() => { if (harmonyPlay) harmonyPlay(); }, 160);
                        setTimeout(() => basePlay(), 240);
                        setTimeout(() => playReverbBurst(), 280);
                    } else if (newStreak >= 10) {
                        // High tier: rapid stutter + harmony
                        setTimeout(() => basePlay(), 70);
                        setTimeout(() => { if (harmonyPlay) harmonyPlay(); }, 140);
                    } else {
                        // Lower milestones: single harmony chord
                        if (harmonyPlay) setTimeout(() => harmonyPlay(), 60);
                    }
                }

            } else {
                playWrong();
                setStreak(0);
            }
        }
    }, [clickedSquare]);


    const generateRandomSquare = () => {
        // Function for generating the next random square
        const letters = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
        const randomLetter = letters[Math.floor(Math.random() * letters.length)];
        const randomNumber = Math.floor(Math.random() * 8) + 1;
        if (randomSquare == `${randomLetter}${randomNumber}`) {
            return generateRandomSquare();
        }
        return `${randomLetter}${randomNumber}`;
    }

    const amICorrect = ({position}) => {
        // Function passed down to the Square so it can know if it was the right one on click
        if (isGameStarted) {
            return position === randomSquare;
        } else {
            return null;
        }

    }

    const startGame = () => {
        // Game starts logic

        setScore(0);
        setTotalClicks(0);
        setSpeed(5);
        setAccuracy(0);

        // Reset streak on new game
        setStreak(0);
        setBannerMessage(null);
        if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);

        setIsGameStarted(true);
        setRandomSquare(generateRandomSquare());
        setTimeLeft(gameLength);

        setGameResults([]);
        setActionStartTime(performance.now());
    }

    const endGame = () => {
        setIsGameStarted(false);
        setRandomSquare(null);
        setStreak(0);
        setBannerMessage(null);
        if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
        analyzeGame(gameResults, userData.id).catch(() => {
            setSaveError(true);
            setTimeout(() => setSaveError(false), 4000);
        });
        console.log("Game Results: ", gameResults);
    }


    const ControlPanel = () => {
        let timeLeftColor = () => {
            if (timeLeft <= 10) {
                return 'error';
            } else if (timeLeft <= 20) {
                return 'warning';
            } else {
                return 'success';
            }
        }
        return (
            <div style={styles.controlPanel}>
            <div style={styles.stats}>
            <div className={`radial-progress text-${timeLeftColor()} mt-auto`}
            style={{ "--value": `${timeLeft*(100/gameLength)}`,
            "--size": "7rem", "--thickness": "1rem" }} role="progressbar">
                <p className="text-xl font-semibold">{timeLeft}s</p></div>
                <h3 className="mt-10" style={styles.score}>{score}</h3>

                {/* --- Streak badge --- */}
                {streak >= 3 && (
                    <animated.div
                        style={{ scale: badgeSpring.scale }}
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full font-bold text-sm mt-4 ${getStreakStyle(streak)}`}
                    >
                        🔥 {streak}
                    </animated.div>
                )}
            </div>
            <div className="mt-auto w-full">
                <div>
                    <div className="flex flex-row w-full justify-between"><p>Accuracy</p><p className="text-lg">{Math.round(accuracy*100)}%</p></div>
                    <progress
                        className={`progress progress-success w-11/12 mt-2`}
                        value={accuracy}
                        max="1"
                    />
                </div>
                <div>
                    <div className="flex flex-row justify-between"><p>Speed</p><p className="text-lg">{speed==5? 0 : Math.round(speed*100)/100}s</p></div>
                    <progress
                        className={`progress progress-success w-11/12 mt-2`}
                        value={5-speed}
                        max="5"
                    />
                </div>
            </div>
            { !isGameStarted &&
            <button className="btn btn-secondary w-11/12 mt-auto" onClick={startGame}><p className="text-stone-100 text-lg">Start</p></button>
            }
        </div>
        )
    }




    return (
        <div style={styles.container}>
            {/* --- Milestone banner --- */}
            <div style={styles.bannerContainer}>
                {bannerTransition((springStyle, item) =>
                    item ? (
                        <animated.div
                            style={springStyle}
                            className="bg-primary text-primary-content rounded-xl px-6 py-3 font-bold text-xl text-center shadow-lg z-50"
                        >
                            {item}
                        </animated.div>
                    ) : null
                )}
            </div>

            <div style={styles.chessboardHolder}>
                <h3 style={styles.randomSquare}>{randomSquare}</h3>
                <Chessboard
                setClickedSquare={setClickedSquare}
                amICorrect={amICorrect}
                boardSize={8}
                />
            </div>
            <ControlPanel />

            {saveError && (
                <div className="alert alert-warning fixed bottom-4 left-1/2 -translate-x-1/2 w-auto z-50 shadow-lg">
                    <span>Score save failed — will retry next game</span>
                </div>
            )}
        </div>


    );
}


const styles = {
    container: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        fontFamily: "Raleway, sans-serif",
        height: '100%',
        width: '100%',
        position: 'relative',
    },
    bannerContainer: {
        position: 'absolute',
        top: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pointerEvents: 'none',
        minWidth: '260px',
    },
    logoImage: {
        position: 'absolute',
        cursor: 'pointer',
        top: '10px',
        left: '10px',
        width: '100px',
        height: '100px',
        zIndex: 1,
    },
    title: {
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        color: 'white',
        fontSize: '3em',
        textAlign: 'center',
        marginTop: '20px',
    },
    chessboardHolder: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    randomSquare: {
        position: 'absolute',
        top: '40%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        color: 'black',
        fontSize: '3.5em',
        opacity: 0.8,
        textAlign: 'center',
        pointerEvents: 'none',
        zIndex: 10,
    },
    controlPanel: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: '5px',
        height: '80vh',
        width: '20vw',
        backgroundColor: '#242424',
        padding: '20px',
    },
    stats: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        width: '30vmin',
    },
    timer: {
        color: 'white',
        fontSize: '3em',
    },
    score: {
        color: 'white',
        fontSize: '6vw',
    },
}

export default Vision;
